import Downloader from "./Downloader";

/**
 * Little explaination on these events:
 * It fires in this order:
 *  - pageleave (before fadout, before change)
 *  - pageleft (after fadeout, before change)
 *  - pageloaded (after load, before change)
 *  - pageenter (after load, after change, before fadein)
 *  - pageready (after load, after change, after fadein)
 */
const PageLeaveEvent = "pageleave";
const PageLeftEvent = "pageleft";
const PageLoadEvent = "pageloaded";
const PageEnterEvent = "pageenter";
const PageReadyEvent = "pageready";

const AppendBefore = "appendbefore";
const AppendAfter = "appendafter";

const Events = [
   PageLeaveEvent,
   PageLeftEvent,
   PageLoadEvent,
   PageEnterEvent,
   PageReadyEvent,

   AppendBefore,
   AppendAfter
];

/**
 * TODO change this into a nice event and stuff
 * @callback PageChangedListener
 * @param { string } pageUrl 
 */

class Pager{
   /**
    * Creates a new pager class with the element as the main container.
    * When you use the goto page it automatically swaps out the page contents
    * @param { Object } options
    * @param { HTMLElement } options.container The main container 
    * @param { boolean } [options.appendHistory] Tells us whether or not we need to use the history  
    * @param { string } [options.containerLoadingClass] The class that is added whilst loading the page
    * @param { number } [options.containerLoadingDelay] The class that is added whilst loading the page
    */
   constructor(options){
      if(options.container === null || options.container === undefined){
         throw new Error("Pager container undefined");
      }

      this.container = options.container;
      this.appendHistory = options.appendHistory || false;
      this.containerLoadingClass = options.containerLoadingClass || "loading";
      this.containerLoadingDelay = options.containerLoadingDelay || -1;

      /**
       * @type { Object.<string, PageChangedListener[]> }
       */
      this.eventListeners = {};

      Events.forEach(event => this.eventListeners[event] = []);

      if(this.appendHistory){
         window.addEventListener('popstate', this._popstateHandler.bind(this));
      }
   }

   /**
    * Loads a new page and swaps out the current page with that page.
    * @param {string} pageUrl 
    * @param {boolean} [preventHistory] Prevents adding to the history
    */
   async goto(pageUrl, preventHistory){
      // TODO add the right (current) url

      // Leave the current page
      this._fireEvent(PageLeaveEvent, "");
      
      let page = Downloader.downloadPage(pageUrl);
      let transitionOut = this._transitionOut();

      // Set the history
      if(this.appendHistory && !preventHistory){
         window.history.pushState("no-data", "", pageUrl)
      }

      // Transition out
      await transitionOut;
      this._fireEvent(PageLeftEvent, "");

      // Load the page
      let pageResult = await page;
      this._fireEvent(PageLoadEvent, pageUrl);
      
      // Set the actual HTML
      this.container.innerHTML = pageResult;
      this._fireEvent(PageEnterEvent, pageUrl);

      
      // Transition in
      await this._transitionIn();
      this._fireEvent(PageReadyEvent, pageUrl);
   }

   /**
    * @param {string} pageUrl 
    */
   async append(pageUrl){
      let page = Downloader.downloadElements(pageUrl);

      this._fireEvent(AppendBefore, pageUrl);

      let result = await page;

      result.forEach(element => {
         this.container.appendChild(element);
      });

      this._fireEvent(AppendAfter, pageUrl);
   }

   /**
    * Possible events are
    * @param { string } event 
    * @param { PageChangedListener } callback 
    */
   addEventListener(event, callback){
      this.eventListeners[event].push(callback);
   }


   _fireEvent(event, pageUrl){
      this.eventListeners[event].forEach(listener => {
         listener(pageUrl);
      });
   }


   /**
    * If you really want you can overwrite these functions for your own 
    * custom transition out and in
    */
   _transitionOut(){
      return new Promise((resolve, _reject) => {
         this.container.classList.add(this.containerLoadingClass);

         if(this.transitionLoadingDelay > 0){
            setTimeout(resolve, this.containerLoadingDelay);
         }else{
            resolve();
         }
      });
   }
   
   _transitionIn(){
      return new Promise((resolve, _reject) => {
         this.container.classList.remove(this.containerLoadingClass);
         
         if(this.transitionLoadingDelay > 0){
            setTimeout(resolve, this.containerLoadingDelay);
         }else{
            resolve();
         }
      });
   }

   _popstateHandler(){
      this.goto(document.location, true);
   }
}

export default Pager;